// CreateEventForm.jsx - Handles NEW event creation with auto-save draft
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import {
  FaAngleLeft,
  FaSpinner,
  FaCheckCircle,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
} from "react-icons/fa";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import confirmDatePlugin from "flatpickr/dist/plugins/confirmDate/confirmDate";
import "flatpickr/dist/plugins/confirmDate/confirmDate.css";
import { uploadFile } from "../../firebase/fileUpload";
import EventAdditionalData from "../../components/eventAdditionalData/EventAdditionalData";
import SponsorDetailForm from "./SponsorDetailForm";
import SponsorParticipationTab from "./SponsorParticipationTab";
import MandatoryFollowsTab from "./MandatoryFollowsTab";
import IndependentMediaUpload from "./IndependentMediaUpload";
import EventFilesForm from "./EventFilesForm";
import EventReferralSettings from "./components/EventReferralSettings";
import OfflineEventSection from "./components/OfflineEventSection";
import { DataContext } from "../context/DataContext";
import { useEventValidation } from "./useEventValidation";
import ValidationReviewModal from "./ValidationReviewModal";
import { estimateCaptionBudget, MAX_CAPTION_LENGTH } from "../../helpers/captionBudget";

const EventType = Object.freeze({
  Short: "short",
  Long: "long",
});

const startDateOptions = { mode: "single", dateFormat: "Y-m-d" };
const endDateOptions = { mode: "single", dateFormat: "Y-m-d" };
const publishStartDateOptions = {
  mode: "single",
  dateFormat: "Y-m-d H:i",
  enableTime: true,
  time_24hr: false,
  altInput: true,
  altFormat: "Y-m-d h:i K",
  plugins: [
    new confirmDatePlugin({
      confirmIcon: "",
      confirmText: "DONE",
      showAlways: true,
      theme: "light"
    })
  ]
};
const publishEndDateOptions = {
  mode: "single",
  dateFormat: "Y-m-d H:i",
  enableTime: true,
  time_24hr: false,
  altInput: true,
  altFormat: "Y-m-d h:i K",
  plugins: [
    new confirmDatePlugin({
      confirmIcon: "",
      confirmText: "DONE",
      showAlways: true,
      theme: "light"
    })
  ]
};

// LocalStorage key for draft
const DRAFT_KEY = "namasvi_event_form_draft";

const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const month = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatDateTimeForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  const month = `0${d.getMonth() + 1}`.slice(-2);
  const day = `0${d.getDate()}`.slice(-2);
  const year = d.getFullYear();
  const hours = `0${d.getHours()}`.slice(-2);
  const mins = `0${d.getMinutes()}`.slice(-2);
  return `${year}-${month}-${day} ${hours}:${mins}`;
};

const parseStrToDate = (str) => {
  if (!str) return "";
  if (str instanceof Date) return str;
  const tMatch = String(str).match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/);
  if (tMatch) {
    return new Date(tMatch[1], tMatch[2] - 1, tMatch[3], tMatch[4], tMatch[5]);
  }
  const dMatch = String(str).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dMatch) {
    return new Date(dMatch[1], dMatch[2] - 1, dMatch[3]);
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const findEventIdDeep = (obj) => {
  const KEYS = new Set(["eventID", "eventId", "event_id", "id"]);
  const seen = new Set();
  const walk = (n) => {
    if (!n || typeof n !== "object" || seen.has(n)) return null;
    seen.add(n);
    for (const k in n) {
      if (KEYS.has(k)) {
        const v = n[k];
        if (v !== undefined && v !== null && String(v).trim() !== "")
          return String(v);
      }
    }
    for (const k in n) {
      const r = walk(n[k]);
      if (r) return r;
    }
    return null;
  };
  return walk(obj);
};

const CreateEventForm = () => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const { prependEvent } = useContext(DataContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    eventName: "",
    description: "",
    startDate: "",
    endDate: "",
    publishStartDate: "",
    publishEndDateTime: "",
    eventType: "",
    termsConditions: "",
    instructions: "",
    sponsorData: [],
    templateData: null,
    additionalData: {
      isPublish: false,
      eventCaption1: "",
      eventCaption2: "",
      eventHashtags: [],
      publishStartDate: "",
      publishEndDateTime: "",
    },
    referralSettings: {
      enableReferral: true,
      referralPercentageOverride: null,
      useCustomPercentage: false,
    },
    offlineConfig: {
      enabled: false,
      venue: { name: "", address: "", city: "", state: "", pincode: "", lat: 0, lng: 0, map_url: "" },
      days: [],
    },
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTemplateUploading, setIsTemplateUploading] = useState(false);

  const [mediaDraft, setMediaDraft] = useState({
    imageData: [],
    videoData: [],
    musicData: [],
    videoOutputDuration: 0,
    musicDuration: 0,
  });

  const [hasDraft, setHasDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showDraftRestored, setShowDraftRestored] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showValidationWarnings, setShowValidationWarnings] = useState(false);

  const mediaRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [sponsorSubTab, setSponsorSubTab] = useState("packages");
  const isLong = (formData.eventType || "").toLowerCase() === EventType.Long;

  // Initialize validation hook
  const {
    getFieldValidation,
    getAllValidationIssues,
    getStepValidationIssues,
  } = useEventValidation(formData, mediaDraft, mediaRef, isLong);

  // Compute hasBlockingIssues for publish mode validation
  const allIssues = getAllValidationIssues();
  const hasBlockingIssues = allIssues.filter(i => i.severity === 'critical').length > 0;

  const stepLabels = [
    "Event Details",
    "Additional Details",
    "Sponsors",
    ...(isLong ? ["Competition Reels"] : []),
    "Required Files",
    "Review & Submit",
  ];
  const totalSteps = stepLabels.length;

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        const parsed = JSON.parse(savedDraft);
        if (parsed.formData) {
          setFormData(parsed.formData);
          setHasDraft(true);
          setLastSaved(parsed.timestamp ? new Date(parsed.timestamp) : null);
          if (parsed.formData.templateData) {
            setImagePreview(parsed.formData.templateData);
          }
          if (parsed.mediaDraft) {
            setMediaDraft(parsed.mediaDraft);
          }
          if (parsed.currentStep) {
            setCurrentStep(parsed.currentStep);
          }
          setShowDraftRestored(true);
          setTimeout(() => setShowDraftRestored(false), 5000);
        }
      }
    } catch (error) {
      console.error("Failed to load draft from localStorage:", error);
    }
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        const draftData = {
          formData,
          mediaDraft,
          currentStep,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(draftData));
        setHasDraft(true);
        setLastSaved(new Date());
      } catch (error) {
        console.error("Failed to save draft to localStorage:", error);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData, mediaDraft, currentStep]);

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const clearDraft = () => {
    if (
      window.confirm(
        "Are you sure you want to clear the saved draft? This action cannot be undone.",
      )
    ) {
      try {
        localStorage.removeItem(DRAFT_KEY);
        setHasDraft(false);
        setLastSaved(null);
        setFormData({
          eventName: "",
          description: "",
          startDate: "",
          endDate: "",
          publishStartDate: "",
          eventType: "",
          termsConditions: "",
          instructions: "",
          sponsorData: [],
          templateData: null,
          additionalData: {
            isPublish: false,
            eventCaption1: "",
            eventCaption2: "",
            eventHashtags: [],
          },
          referralSettings: {
            enableReferral: true,
            referralPercentageOverride: null,
            useCustomPercentage: false,
          },
          offlineConfig: {
            enabled: false,
            venue: { name: "", address: "", city: "", state: "", pincode: "", lat: 0, lng: 0, map_url: "" },
            days: [],
          },
        });
        setMediaDraft({
          imageData: [],
          videoData: [],
          musicData: [],
          videoOutputDuration: 0,
          musicDuration: 0,
        });
        setImagePreview(null);
        setCurrentStep(1);
        alert("Draft cleared successfully!");
      } catch (error) {
        console.error("Failed to clear draft:", error);
        alert("Failed to clear draft. Please try again.");
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      additionalData: {
        ...(prev.additionalData || {}),
        isPublish: checked,
      },
    }));
  };

  // Helper functions for media payload building
  const isBlobUrl = (s) => typeof s === "string" && s.startsWith("blob:");

  const toInt = (v) => {
    const x = parseInt(String(v ?? 0), 10);
    return Number.isFinite(x) ? x : 0;
  };

  const toPositiveInt = (v) => {
    const x = parseInt(String(v ?? ""), 10);
    return Number.isFinite(x) && x > 0 ? x : null;
  };

  const sanitizeMediaList = (arr = []) =>
    (Array.isArray(arr) ? arr : [])
      .map((it) => {
        if (!it) return null;
        const label = String(it.label ?? "").trim();
        const url = String(it.url ?? "").trim();
        if (!url || isBlobUrl(url)) return null;
        return {
          label,
          url,
          description: String(it.description ?? ""),
          duration: toInt(it.duration),
          mandatory: !!it.mandatory,
          default: !!it.default,
          dontMute: !!it.dontMute,
        };
      })
      .filter(Boolean);

  const buildMediaPayload = (refInstance, draft) => {
    if (refInstance && typeof refInstance.getPayload === "function") {
      const p = refInstance.getPayload();
      const images = (() => {
        try {
          return JSON.parse(p.image_data || "[]");
        } catch {
          return [];
        }
      })();
      const videos = (() => {
        try {
          return JSON.parse(p.video_data || "[]");
        } catch {
          return [];
        }
      })();
      const music = (() => {
        try {
          return JSON.parse(p.music_data || "[]");
        } catch {
          return [];
        }
      })();
      const imgSan = sanitizeMediaList(images);
      const vidSan = sanitizeMediaList(videos);
      const musSan = sanitizeMediaList(music);
      return {
        number_of_images: imgSan.length,
        number_of_videos: vidSan.length,
        number_of_music: musSan.length,
        image_data: JSON.stringify(imgSan),
        video_data: JSON.stringify(vidSan),
        music_data: JSON.stringify(musSan),
        video_output_duration_in_secs: toInt(
          p.video_output_duration_in_secs ?? p.videoOutputDuration
        ),
        music_duration_in_secs: toInt(
          p.music_duration_in_secs ?? p.musicDuration
        ),
      };
    }

    const imgSan = sanitizeMediaList(draft.imageData || []);
    const vidSan = sanitizeMediaList(draft.videoData || []);
    const musSan = sanitizeMediaList(draft.musicData || []);
    return {
      number_of_images: imgSan.length,
      number_of_videos: vidSan.length,
      number_of_music: musSan.length,
      image_data: JSON.stringify(imgSan),
      video_data: JSON.stringify(vidSan),
      music_data: JSON.stringify(musSan),
      video_output_duration_in_secs: toInt(draft.videoOutputDuration),
      music_duration_in_secs: toInt(draft.musicDuration),
    };
  };

  const handleTextareaResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  // Hard validation - used for publish mode, blocks progression
  const validateStep1_EventBasics = () => {
    const issues = getStepValidationIssues(1);
    if (issues.length > 0) {
      setCurrentStep(1);
      setShowValidationWarnings(true);
      setTimeout(() => {
        const errorEl = document.querySelector(`.error-field-${issues[0].field}`) || document.querySelector('.error-field');
        if (errorEl) errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return false;
    }
    return true;
  };

  const validateStep2_Additional = () => {
    const issues = getStepValidationIssues(2);
    if (issues.length > 0) {
      setCurrentStep(2);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  const validateStep3_Sponsors = () => {
    const issues = getStepValidationIssues(3);
    if (issues.length > 0) {
      setCurrentStep(3);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  const validateStep4_Template = () => {
    if (isTemplateUploading) {
      setShowValidationWarnings(true);
      return false;
    }
    const issues = getStepValidationIssues(4);
    if (issues.length > 0) {
      setCurrentStep(4);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  const validateStep4_MediaIfLong = () => {
    if (!isLong) return true;
    const issues = getStepValidationIssues(4);
    if (issues.length > 0) {
      setCurrentStep(4);
      setShowValidationWarnings(true);
      return false;
    }
    return true;
  };

  // Allow skipping with confirmation if there are issues
  const nextStep = (forceSkip = false) => {
    const stepIssues = getStepValidationIssues(currentStep);

    if (stepIssues.length > 0 && !forceSkip) {
      setShowValidationWarnings(true);

      const firstIssue = stepIssues[0];
      const fieldName = firstIssue.field;

      setTimeout(() => {
        const errorEl = document.querySelector(`.error-field-${fieldName}`) || document.querySelector('.error-field');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      return;
    }

    setShowValidationWarnings(false);
    setCurrentStep((s) => Math.min(s + 1, totalSteps));
  };

  const prevStep = () => setCurrentStep((s) => Math.max(s - 1, 1));

  // Allow direct navigation without blocking
  const goToStep = (n) => {
    setCurrentStep(n);
  };

  // Handle submit button click - opens review modal
  const handleSubmitClick = () => {
    setShowReviewModal(true);
  };

  // Actual submission after review
  const handleConfirmedSubmit = async () => {
    setShowReviewModal(false);
    await handleCreateSubmit();
  };

  const handleTemplateUpload = async (file) => {
    try {
      if (!file) return null;
      setImagePreview(URL.createObjectURL(file));
      setIsTemplateUploading(true);

      console.log("Starting template upload for file:", file.name, "Size:", file.size);
      const uploadedUrl = await uploadFile(file);
      console.log("Template upload successful. URL:", uploadedUrl);

      setFormData((prev) => ({ ...prev, templateData: uploadedUrl }));
      return uploadedUrl;
    } catch (error) {
      console.error("Template upload failed - Full error:", error);
      console.error("Error message:", error.message);
      console.error("Error code:", error.code);

      // Clear the preview on error
      setImagePreview(null);

      // Show detailed error message
      let errorMessage = "Error uploading template file. ";
      if (error.code === 'storage/unauthorized') {
        errorMessage += "Permission denied. Please check Firebase storage rules.";
      } else if (error.code === 'storage/canceled') {
        errorMessage += "Upload was cancelled.";
      } else if (error.code === 'storage/unknown') {
        errorMessage += "An unknown error occurred. Please try again.";
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += "Please try again.";
      }

      alert(errorMessage);
      return null;
    } finally {
      setIsTemplateUploading(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e?.preventDefault?.();
    if (isSubmitting) return;

    const isPublishMode = !!formData?.additionalData?.isPublish;

    try {
      setIsSubmitting(true);

      // For publish mode, all validations must pass
      if (isPublishMode) {
        if (!validateStep1_EventBasics()) return;
        if (!validateStep2_Additional()) return;
        if (!validateStep3_Sponsors()) return;
        if (isLong && !validateStep4_MediaIfLong()) return;
      } else {
        // For draft mode, only check critical issues (template upload status)
        if (isTemplateUploading) {
          setShowValidationWarnings(true);
          return;
        }
        if (formData.templateData && String(formData.templateData).startsWith("blob:")) {
          setShowValidationWarnings(true);
          return;
        }
      }

      // Check for authentication token - prioritize fbauthToken (real FB token) over authToken (admin token)
      const accessToken = Cookies.get("fbauthToken") || Cookies.get("authToken");
      if (!accessToken) {
        alert("Not authenticated. Please log in.");
        return;
      }

      const eventPayload = {
        eventName: formData.eventName,
        description: formData.description,
        eventType: formData.eventType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        termsConditions: formData.termsConditions,
        instructions: formData.instructions,
        templateData: formData.templateData,
        additionalData: {
          ...formData.additionalData,
          publishStartDate: formData.publishStartDate,
          publishEndDateTime: formData.publishEndDateTime,
        },
        sponsorData: Array.isArray(formData.sponsorData) ? formData.sponsorData : [],
        referralSettings: formData.referralSettings,
      };

      const eventResp = await axios.post(
        `${apiDomain}/api/v2/createevent?access_token=${accessToken}`,
        eventPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (![200, 201].includes(eventResp?.status)) {
        throw new Error(`Event save failed (${eventResp?.status})`);
      }

      const createdEvent = eventResp.data;
      const createdEventId = findEventIdDeep(createdEvent);

      if (!createdEventId) {
        throw new Error("No eventId in response");
      }

      const normalizedEvent = {
        ...createdEvent,
        eventID: createdEventId,
        // backend may not echo publish dates, keep the local values
        publishStartDate: createdEvent.publishStartDate || formData.publishStartDate || '',
        publishEndDateTime: createdEvent.publishEndDateTime || formData.publishEndDateTime || '',
        additionalData: {
          ...createdEvent.additionalData,
          publishStartDate: formData.publishStartDate,
          publishEndDateTime: formData.publishEndDateTime,
        },
      };

      // Note: Sponsors are already included in the main event payload above
      // No separate sponsor POST needed (unlike the old /events REST endpoint)

      // Media handling for long events - create media record after event is created
      if (isLong) {
        const mediaPayload = buildMediaPayload(mediaRef.current, mediaDraft);
        const createdEventIdNum = toPositiveInt(createdEventId);

        if (createdEventIdNum) {
          // Add event_id to payload - backend requires it in the body
          const mediaPayloadWithId = {
            ...mediaPayload,
            event_id: createdEventIdNum,
          };

          console.log('=== Creating media for long event ===');
          console.log('Event ID:', createdEventIdNum);
          console.log('Media payload:', mediaPayloadWithId);

          try {
            const mediaResp = await axios.post(
              `${apiDomain}/api/v2/longevent/createorupdate?access_token=${accessToken}`,
              mediaPayloadWithId,
              {
                headers: {
                  "Content-Type": "application/json",
                },
              },
            );

            console.log('Media response:', mediaResp.status, mediaResp.data);

            if (![200, 201].includes(mediaResp?.status)) {
              console.error(`Media creation failed (${mediaResp?.status})`);
              // Don't throw - event was created successfully, just media failed
              alert("Event created but media upload failed. You can add media by editing the event.");
            }
          } catch (mediaError) {
            console.error("Media creation error:", mediaError);
            // Don't throw - event was created successfully, just media failed
            alert("Event created but media upload failed. You can add media by editing the event.");
          }
        } else {
          console.warn("Could not get valid event ID for media creation");
        }
      }

      // Persist offline-event config (PUT /api/v2/admin/events/offline-config)
      // only when the toggle is on AND we have a numeric event id. Failures
      // here are surfaced but the event is already saved — never block the
      // create flow on the secondary write.
      if (formData.offlineConfig?.enabled) {
        try {
          await axios.put(
            `${apiDomain}/api/v2/admin/events/offline-config?event_id=${createdEventId}&access_token=${accessToken}`,
            {
              enabled: true,
              venue: formData.offlineConfig.venue || {},
              days: Array.isArray(formData.offlineConfig.days) ? formData.offlineConfig.days : [],
            },
            { headers: { "Content-Type": "application/json" } },
          );
        } catch (cfgErr) {
          console.error("offline-config save failed:", cfgErr);
          alert("Event created, but saving offline check-in config failed. Edit the event to retry.");
        }
      }

      try {
        localStorage.removeItem(DRAFT_KEY);
        setHasDraft(false);
        setLastSaved(null);
      } catch (error) {
        console.error("Failed to clear draft after submission:", error);
      }

      alert("Event created successfully!");

      prependEvent?.(normalizedEvent);
      navigate("/events", { replace: true, state: { newEvent: normalizedEvent } });
    } catch (error) {
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        (typeof error?.response?.data === "string" ? error?.response?.data : null);
      const displayMsg = serverMsg || error?.message || "An error occurred";
      alert(`Failed to create event: ${displayMsg}`);
      console.error("Event creation failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to get validation highlight class for a field
  const getValidationClass = (fieldName, baseClass = "") => {
    if (!showValidationWarnings) return baseClass;
    const validation = getFieldValidation(fieldName);
    if (!validation.isValid && validation.required) {
      return `${baseClass} border-red-500 ring-1 ring-red-500 bg-red-50 error-field-${fieldName}`;
    }
    return baseClass;
  };

  // Render validation warning banner for current step
  const renderStepValidationWarning = () => {
    const stepIssues = getStepValidationIssues(currentStep);
    if (stepIssues.length === 0) return null;

    return (
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-3">
        <FaExclamationTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-yellow-800">
            {stepIssues.length} field{stepIssues.length > 1 ? "s" : ""} need{stepIssues.length === 1 ? "s" : ""} attention
          </p>
          <p className="text-sm text-yellow-700">
            Complete these fields before publishing, or continue to save as draft.
          </p>
        </div>
        <button
          type="button"
          onClick={() => nextStep(true)}
          className="text-yellow-700 hover:text-yellow-900 text-sm underline"
        >
          Skip for now
        </button>
      </div>
    );
  };

  // Render functions (simplified - showing only essential parts)
  const renderStepEventDetails = () => (
    <div className="w-full space-y-6">
      <h3 className="text-xl font-semibold text-blue-600">Event Details</h3>
      {showValidationWarnings && renderStepValidationWarning()}

      {/* ── Group 1: Basic Information ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Basic Information
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Event Type <span className="text-red-500">*</span>
            </span>
            <select
              name="eventType"
              value={formData.eventType}
              onChange={(e) => {
                handleChange(e);
              }}
              className={getValidationClass("eventType", "w-full h-12 border rounded px-4")}
            >
              <option value="">Select Event Type</option>
              <option value="short">Short</option>
              <option value="long">Long</option>
            </select>
            {showValidationWarnings && !getFieldValidation("eventType").isValid && (
              <span className="text-red-600 text-sm">⚠ Event type is required</span>
            )}
          </div>

          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Event Name <span className="text-red-500">*</span>
            </span>
            <input
              type="text"
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder="Enter Event Name"
              className={getValidationClass("eventName", "w-full h-12 border rounded px-4")}
            />
            {showValidationWarnings && !getFieldValidation("eventName").isValid && (
              <span className="text-red-600 text-sm">⚠ Event name is required</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Pricing ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Pricing
        </h4>
        <div className="flex flex-col items-start space-y-2">
          <span className="starlabel font-medium text-lg">Event Price</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={
              formData.additionalData?.eventPrice === null ||
                formData.additionalData?.eventPrice === undefined
                ? ""
                : formData.additionalData.eventPrice
            }
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, "");
              setFormData((prev) => ({
                ...prev,
                additionalData: {
                  ...(prev.additionalData || {}),
                  eventPrice: v === "" ? null : Number(v),
                },
              }));
            }}
            onKeyDown={(e) => {
              if (
                !/^\d$/.test(e.key) &&
                !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
                !e.ctrlKey &&
                !e.metaKey
              )
                e.preventDefault();
            }}
            onFocus={(e) => e.target.select()}
            className="w-full h-12 border rounded px-4"
            placeholder="Enter price (0 = free)"
          />
        </div>
      </div>

      {/* ── Group 2: Event Schedule ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Event Schedule
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Start Date <span className="text-red-500">*</span>
            </span>
            <Flatpickr
              value={parseStrToDate(formData.startDate)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  startDate: dates[0] ? formatDateForInput(dates[0]) : "",
                }));
              }}
              options={startDateOptions}
              className={getValidationClass("startDate", "w-full h-12 border rounded px-4")}
              placeholder="Select Start Date"
            />
            {showValidationWarnings && !getFieldValidation("startDate").isValid && (
              <span className="text-red-600 text-sm">⚠ Start date is required</span>
            )}
          </div>

          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              End Date <span className="text-red-500">*</span>
            </span>
            <Flatpickr
              value={parseStrToDate(formData.endDate)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  endDate: dates[0] ? formatDateForInput(dates[0]) : "",
                }));
              }}
              options={endDateOptions}
              className={getValidationClass("endDate", "w-full h-12 border rounded px-4")}
              placeholder="Select End Date"
            />
            {showValidationWarnings && !getFieldValidation("endDate").isValid && (
              <span className="text-red-600 text-sm">⚠ End date is required</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Group 3: Publishing Schedule ── */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h4 className="text-base font-semibold text-gray-700 mb-1 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />
          Publishing Schedule
          <span className="text-gray-400 text-sm font-normal">(optional – for post/reels)</span>
        </h4>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Publish/Voting Start Date & Time
            </span>
            <Flatpickr
              value={parseStrToDate(formData.publishStartDate)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  publishStartDate: dates[0] ? formatDateTimeForInput(dates[0]) : "",
                }));
              }}
              options={publishStartDateOptions}
              className="w-full h-12 border rounded px-4"
              placeholder="Select Publish Start Date & Time"
            />
          </div>

          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Publish/Voting End Date & Time
            </span>
            <Flatpickr
              value={parseStrToDate(formData.publishEndDateTime)}
              onChange={(dates) => {
                setFormData((prev) => ({
                  ...prev,
                  publishEndDateTime: dates[0] ? formatDateTimeForInput(dates[0]) : "",
                }));
              }}
              options={publishEndDateOptions}
              className="w-full h-12 border rounded px-4"
              placeholder="Select Publish End Date & Time"
            />
          </div>
        </div>
      </div>

      {/* ── Group 4: Event Content ── */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
        <h4 className="text-base font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
          Event Content
        </h4>
        <div className="space-y-6">
          <div className="flex flex-col items-start space-y-2">
            <span className="starlabel font-medium text-lg">
              Event Description <span className="text-red-500">*</span>
            </span>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onInput={handleTextareaResize}
              placeholder="Enter Event Description"
              className={getValidationClass("description", "w-full lg:min-h-40 border rounded px-4 py-2")}
            />
            {showValidationWarnings && !getFieldValidation("description").isValid && (
              <span className="text-red-600 text-sm">⚠ Description is required</span>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col items-start space-y-2">
              <span className="starlabel font-medium text-lg">
                Terms & Conditions <span className="text-red-500">*</span>
              </span>
              <textarea
                name="termsConditions"
                value={formData.termsConditions}
                onChange={handleChange}
                onInput={handleTextareaResize}
                placeholder="Enter Terms & Conditions"
                className={getValidationClass("termsConditions", "w-full lg:min-h-40 border rounded px-4 py-2")}
              />
              {showValidationWarnings && !getFieldValidation("termsConditions").isValid && (
                <span className="text-red-600 text-sm">⚠ Terms & Conditions are required</span>
              )}
            </div>

            <div className="flex flex-col items-start space-y-2">
              <span className="starlabel font-medium text-lg">
                Instructions <span className="text-red-500">*</span>
              </span>
              <textarea
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                onInput={handleTextareaResize}
                placeholder="Enter Instructions"
                className={getValidationClass("instructions", "w-full lg:min-h-40 border rounded px-4 py-2")}
              />
              {showValidationWarnings && !getFieldValidation("instructions").isValid && (
                <span className="text-red-600 text-sm">⚠ Instructions are required</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Group 5: Referral Settings ── */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <EventReferralSettings
          referralSettings={formData.referralSettings}
          onChange={(settings) =>
            setFormData((prev) => ({ ...prev, referralSettings: settings }))
          }
        />
      </div>

      {/* ── Group 6: Offline Event Check-In ── */}
      <OfflineEventSection
        value={formData.offlineConfig}
        onChange={(cfg) =>
          setFormData((prev) => ({ ...prev, offlineConfig: cfg }))
        }
      />

    </div>
  );

  const renderStepAdditional = () => (
    <EventAdditionalData
      additionalData={formData.additionalData}
      setAdditionalData={(data) =>
        setFormData((prev) => ({
          ...prev,
          additionalData: {
            ...data,
            // Preserve fields NOT managed by EventAdditionalData
            isPublish: prev.additionalData?.isPublish ?? false,
            // sponsorParticipationPost lives in the Sponsors tab – never let
            // EventAdditionalData's sync overwrite it.
            sponsorParticipationPost: prev.additionalData?.sponsorParticipationPost,
            // Competition Reel frame lives in the Competition Reels tab
            competitionReelFrameImage: prev.additionalData?.competitionReelFrameImage,
            // Required Files (event-level) — managed in Required Files tab
            requiredFiles: prev.additionalData?.requiredFiles,
            requiredFilesEnabled: prev.additionalData?.requiredFilesEnabled,
            // Sponsor Required Files — managed in Sponsors > Sponsor Required Files tab
            sponsorRequiredFiles: prev.additionalData?.sponsorRequiredFiles,
            sponsorRequiredFilesEnabled: prev.additionalData?.sponsorRequiredFilesEnabled,
            // Post Carousel — managed in Competition Reels tab
            isPostCarousel: prev.additionalData?.isPostCarousel,
            postCarouselConfig: prev.additionalData?.postCarouselConfig,
            // Sponsor Terms & Conditions — global, managed in Sponsors tab
            sponsorTermsConditions: prev.additionalData?.sponsorTermsConditions,
            // Sponsor Participation Carousel — managed in Sponsors > Participation tab
            isSponsorParticipationCarousel: prev.additionalData?.isSponsorParticipationCarousel,
            sponsorParticipationCarouselConfig: prev.additionalData?.sponsorParticipationCarouselConfig,
          },
        }))
      }
      isEditMode={false}
      onFinish={() => setCurrentStep(3)}
      templateData={formData.templateData}
      onTemplateDataChange={(url) =>
        setFormData((prev) => ({ ...prev, templateData: url }))
      }
    />
  );

  const renderStepSponsors = () => (
    <div>
      {/* Sub-tab bar */}
      <div className="flex border-b border-gray-200 mb-6">
        {[
          { id: "packages", label: "📦 Sponsor Packages" },
          { id: "participation", label: "📸 Participation Post" },
          { id: "mandatory", label: "🤝 Mandatory Follows" },
          { id: "sponsorFiles", label: "📄 Sponsor Required Files" },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setSponsorSubTab(tab.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${sponsorSubTab === tab.id
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {sponsorSubTab === "packages" && (
        <SponsorDetailForm
          savedCards={formData.sponsorData}
          sponsorTermsConditions={formData.additionalData?.sponsorTermsConditions || ""}
          onTermsConditionsChange={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                sponsorTermsConditions: val,
              },
            }))
          }
          onSponsorDataChange={(data) =>
            setFormData((prev) => ({
              ...prev,
              sponsorData: [...(prev.sponsorData || []), data],
            }))
          }
          onDeleteSponsor={(index) =>
            setFormData((prev) => ({
              ...prev,
              sponsorData: (prev.sponsorData || []).filter((_, i) => i !== index),
            }))
          }
        />
      )}

      {sponsorSubTab === "participation" && (
        <SponsorParticipationTab
          ppData={formData.additionalData?.sponsorParticipationPost || {}}
          onDataChange={(updatedPP) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                sponsorParticipationPost: updatedPP,
              },
            }))
          }
          // Sponsor carousel config is separate from user participation carousel.
          isSponsorParticipationCarousel={formData.additionalData?.isSponsorParticipationCarousel || false}
          setIsSponsorParticipationCarousel={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: { ...(prev.additionalData || {}), isSponsorParticipationCarousel: val },
            }))
          }
          sponsorParticipationCarouselConfig={
            formData.additionalData?.sponsorParticipationCarouselConfig || {
              adminMediaCount: 0,
              userMediaCount: 0,
              mediaItems: [],
            }
          }
          setParticipationCarouselConfig={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: {
                ...(prev.additionalData || {}),
                sponsorParticipationCarouselConfig: val,
              },
            }))
          }
        />
      )}

            {sponsorSubTab === "mandatory" && (
            <MandatoryFollowsTab
            initialAccounts={formData.additionalData?.mandatoryFollows || []}
            onDataChange={(updatedAccounts) =>
              setFormData((prev) => ({
                ...prev,
                additionalData: {
                  ...(prev.additionalData || {}),
                  mandatoryFollows: updatedAccounts,
                },
              }))
            }
            />
            )}

      {sponsorSubTab === "sponsorFiles" && (
        <EventFilesForm
          files={formData.additionalData?.sponsorRequiredFiles || []}
          setFiles={(files) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: { ...prev.additionalData, sponsorRequiredFiles: files },
            }))
          }
          enabled={!!formData.additionalData?.sponsorRequiredFilesEnabled}
          setEnabled={(val) =>
            setFormData((prev) => ({
              ...prev,
              additionalData: { ...prev.additionalData, sponsorRequiredFilesEnabled: val },
            }))
          }
        />
      )}
    </div>
  );

  const renderStepTemplate = () => (
    <div className="w-full p-4 border rounded-md mt-4 bg-blue-50 shadow-md">
      <h3 className="text-xl lg:text-2xl font-semibold text-gray-700">
        Template Frame Image
      </h3>
      <div className="mt-2 flex items-center gap-3">
        <input
          type="file"
          accept="image/*"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (file) await handleTemplateUpload(file);
          }}
          disabled={isTemplateUploading}
          className="w-fit bg-white rounded-lg p-4 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {isTemplateUploading && (
          <span className="inline-flex items-center text-sm text-blue-700 font-medium">
            <FaSpinner className="mr-2 animate-spin" /> Uploading template…
          </span>
        )}
        {!isTemplateUploading && formData.templateData && (
          <span className="inline-flex items-center text-sm text-green-700 font-medium">
            <FaCheckCircle className="mr-2" /> Upload successful!
          </span>
        )}
      </div>
      {imagePreview && (
        <div className="mt-4">
          <img
            src={imagePreview}
            alt="Template Preview"
            className="w-60 h-60 lg:w-96 lg:h-96 object-cover rounded border-2 border-gray-300"
          />
        </div>
      )}
      {!formData.templateData && !isTemplateUploading && (
        <div className={`mt-2 p-2 rounded ${getValidationClass("templateData", "")}`}>
          <p className="text-sm text-gray-600">
            ⚠ Please upload a template frame image to continue.
          </p>
        </div>
      )}
    </div>
  );

  const renderStepFiles = () => (
    <EventFilesForm
      files={formData.additionalData?.requiredFiles || []}
      setFiles={(files) =>
        setFormData((prev) => ({
          ...prev,
          additionalData: { ...prev.additionalData, requiredFiles: files },
        }))
      }
      enabled={!!formData.additionalData?.requiredFilesEnabled}
      setEnabled={(val) =>
        setFormData((prev) => ({
          ...prev,
          additionalData: { ...prev.additionalData, requiredFilesEnabled: val },
        }))
      }
    />
  );

  const renderStepMedia = () => (
    <div className="mt-4">
      {isLong && showValidationWarnings && getStepValidationIssues(4).length > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-start gap-3">
          <FaExclamationTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-yellow-800">Media required for long events</p>
            <p className="text-sm text-yellow-700">
              Add at least one image, video, or music track.
            </p>
          </div>
        </div>
      )}
      <IndependentMediaUpload
        ref={mediaRef}
        eventId={null}
        draft={mediaDraft}
        onDraftChange={setMediaDraft}
        ppCaption2={formData?.additionalData?.participationPost?.caption2 || ""}
        onPpCaption2Change={(val) =>
          setFormData((prev) => ({
            ...prev,
            additionalData: {
              ...(prev.additionalData || {}),
              participationPost: {
                ...(prev.additionalData?.participationPost || {}),
                caption2: val,
              },
            },
          }))
        }
        isPostCarousel={formData?.additionalData?.isPostCarousel || false}
        setIsPostCarousel={(val) =>
          setFormData((prev) => ({
            ...prev,
            additionalData: {
              ...(prev.additionalData || {}),
              isPostCarousel: val
            }
          }))
        }
        postCarouselConfig={formData?.additionalData?.postCarouselConfig || { adminMediaCount: 0, userMediaCount: 0, mediaItems: [] }}
        setPostCarouselConfig={(val) =>
          setFormData((prev) => ({
            ...prev,
            additionalData: {
              ...(prev.additionalData || {}),
              postCarouselConfig: val
            }
          }))
        }
        frameImageUrl={formData?.additionalData?.competitionReelFrameImage || null}
        onFrameImageUrlChange={(url) =>
          setFormData((prev) => ({
            ...prev,
            additionalData: {
              ...(prev.additionalData || {}),
              competitionReelFrameImage: url,
            },
          }))
        }
      />
    </div>
  );

  const renderStepReview = () => {
    const allIssues = getAllValidationIssues();
    const isPublishMode = !!formData?.additionalData?.isPublish;
    const hasBlockingIssues = isPublishMode && allIssues.length > 0;

    return (
      <div className="w-full p-4 border rounded-md bg-blue-50">
        <h3 className="text-xl font-semibold text-blue-600 mb-4">
          Review & Submit
        </h3>

        {/* Validation Summary */}
        {allIssues.length > 0 && (
          <div className={`mb-4 p-4 rounded-lg border ${isPublishMode ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-300'}`}>
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className={isPublishMode ? "text-red-500" : "text-yellow-500"} size={20} />
              <div className="flex-1">
                <p className={`font-semibold ${isPublishMode ? 'text-red-800' : 'text-yellow-800'}`}>
                  {allIssues.length} field{allIssues.length > 1 ? 's' : ''} need{allIssues.length === 1 ? 's' : ''} attention
                </p>
                <ul className="mt-2 text-sm space-y-1">
                  {allIssues.slice(0, 5).map((issue, idx) => (
                    <li key={idx} className={isPublishMode ? 'text-red-700' : 'text-yellow-700'}>
                      • Step {issue.step}: {issue.message}
                      <button
                        type="button"
                        onClick={() => goToStep(issue.step)}
                        className="ml-2 text-blue-600 hover:underline text-xs"
                      >
                        Fix it →
                      </button>
                    </li>
                  ))}
                  {allIssues.length > 5 && (
                    <li className="text-gray-600">...and {allIssues.length - 5} more</li>
                  )}
                </ul>
                {isPublishMode && (
                  <p className="mt-2 text-sm text-red-600 font-medium">
                    ⚠ You must complete all required fields to publish this event.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {allIssues.length === 0 && (
          <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded-lg">
            <div className="flex items-center gap-3">
              <FaCheckCircle className="text-green-500" size={20} />
              <div>
                <p className="font-semibold text-green-800">All fields are complete!</p>
                <p className="text-sm text-green-700">Your event is ready to be {isPublishMode ? 'published' : 'saved'}.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Basic Details</h4>
              <button
                type="button"
                onClick={() => goToStep(1)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className={!getFieldValidation("eventType").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Type:</b> {formData.eventType || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("eventName").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Name:</b> {formData.eventName || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("description").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Description:</b> {formData.description ? (formData.description.length > 50 ? formData.description.substring(0, 50) + "..." : formData.description) : <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("startDate").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Start Date:</b> {formData.startDate || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
              <li className={!getFieldValidation("endDate").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>End Date:</b> {formData.endDate || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Additional Info</h4>
              <button
                type="button"
                onClick={() => goToStep(2)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li className={!getFieldValidation("eventCaption2").isValid ? "text-yellow-700 bg-yellow-50 p-1 rounded" : ""}>
                <b>Caption 2:</b> {formData.additionalData?.eventCaption2 || <span className="text-yellow-600">Not set ⚠</span>}
              </li>
            </ul>
          </div>

          {(() => {
            const budget = estimateCaptionBudget({
              eventCaption1: formData.additionalData?.participationPost?.caption1 || "",
              eventCaption2: formData.additionalData?.eventCaption2 || "",
              hashtags: formData.additionalData?.participationPost?.hashtags || [],
            });
            const over = budget.isOver;
            const critical = budget.isCritical;
            const box = over
              ? "border-red-400 bg-red-50"
              : critical
              ? "border-yellow-400 bg-yellow-50"
              : "border-green-300 bg-green-50";
            return (
              <div className={`bg-white rounded-lg border-2 ${box} p-4 lg:col-span-2`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">
                    Caption Length Budget · Instagram limit {MAX_CAPTION_LENGTH}
                  </h4>
                  <button
                    type="button"
                    onClick={() => goToStep(2)}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Edit captions
                  </button>
                </div>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li><b>Participation post caption:</b> {budget.cap1Len} chars</li>
                  <li><b>Hashtag block:</b> {budget.hashtagBlockLen} chars</li>
                  <li><b>Referral code block:</b> ~{budget.refBlockLen} chars (depends on participant)</li>
                  <li className="pt-1 border-t">
                    <b>Estimated total before participant text:</b>{" "}
                    <span className={over ? "text-red-700 font-bold" : critical ? "text-yellow-700 font-bold" : "text-green-700 font-bold"}>
                      {budget.adminOverhead}/{MAX_CAPTION_LENGTH}
                    </span>
                  </li>
                  <li>
                    <b>Budget left for participant's own text:</b>{" "}
                    <span className={budget.userBudgetRemaining < 200 ? "text-red-700 font-bold" : "text-gray-800"}>
                      {budget.userBudgetRemaining} chars
                    </span>
                  </li>
                </ul>
                {over && (
                  <p className="mt-2 text-sm text-red-700 font-medium">
                    ⚠ Combined caption already exceeds Instagram's {MAX_CAPTION_LENGTH}-char limit. Shorten the Participation post caption before publishing — participants will get a "caption too long" rejection from Meta.
                  </p>
                )}
                {!over && critical && (
                  <p className="mt-2 text-sm text-yellow-700">
                    ⚠ Very little room left for the participant's own text. Consider trimming the Participation post caption.
                  </p>
                )}
              </div>
            );
          })()}

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Sponsors</h4>
              <button
                type="button"
                onClick={() => goToStep(3)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <div className={!getFieldValidation("sponsorData").isValid ? "text-yellow-700 bg-yellow-50 p-2 rounded" : ""}>
              <b>Sponsors:</b> {Array.isArray(formData.sponsorData) && formData.sponsorData.length > 0
                ? `${formData.sponsorData.length} added`
                : <span className="text-yellow-600">None added ⚠</span>}
            </div>
          </div>

          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Template</h4>
              <button
                type="button"
                onClick={() => goToStep(2)}
                className="text-blue-600 hover:underline text-sm"
              >
                Edit
              </button>
            </div>
            <div className={!getFieldValidation("templateData").isValid ? "text-yellow-700 bg-yellow-50 p-2 rounded" : ""}>
              <b>Template Image:</b> {formData.templateData
                ? <span className="text-green-600">✓ Uploaded</span>
                : <span className="text-yellow-600">Not uploaded ⚠</span>}
            </div>
            {formData.templateData && (
              <img
                src={formData.templateData}
                alt="Template"
                className="mt-2 w-20 h-20 object-cover rounded border"
              />
            )}
          </div>
        </div>

        <div className="w-full mt-6 p-4 bg-white rounded-lg border">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!formData?.additionalData?.isPublish}
              onChange={handleCheckboxChange}
              className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <span className="text-gray-700 font-medium">Publish Event</span>
              <p className="text-sm text-gray-500">
                {formData?.additionalData?.isPublish
                  ? "Event will be visible to users immediately after creation."
                  : "Event will be saved as draft. You can publish later."}
              </p>
            </div>
          </label>
          {hasBlockingIssues && (
            <p className="mt-2 text-sm text-red-600">
              ⚠ Cannot publish: Complete all {allIssues.length} required field{allIssues.length > 1 ? 's' : ''} first.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep === 1) return renderStepEventDetails();
    if (currentStep === 2) return renderStepAdditional();
    if (currentStep === 3) return renderStepSponsors();
    if (isLong) {
      if (currentStep === 4) return renderStepMedia();
      if (currentStep === 5) return renderStepFiles();
      if (currentStep === 6) return renderStepReview();
    } else {
      if (currentStep === 4) return renderStepFiles();
      if (currentStep === 5) return renderStepReview();
    }
    return renderStepReview();
  };

  const labelAt = (i) => stepLabels[i - 1] || "";

  const StepPill = ({ index, label }) => {
    const done = index < currentStep;
    const active = index === currentStep;
    const stepIssues = getStepValidationIssues(index);
    const hasIssues = stepIssues.length > 0;

    return (
      <button
        type="button"
        onClick={() => goToStep(index)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${active
          ? "bg-blue-600 text-white border border-blue-600"
          : done && !hasIssues
            ? "bg-green-100 text-green-700 border border-green-300"
            : done && hasIssues
              ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
              : "bg-gray-50 text-gray-700 border-gray-300"
          }`}
      >
        {done && !hasIssues ? (
          <FaCheckCircle className="text-green-600" />
        ) : done && hasIssues ? (
          <FaExclamationTriangle className="text-yellow-600" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-current inline-block" />
        )}
        <span className="font-medium">{label}</span>
        {hasIssues && (
          <span className="bg-yellow-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {stepIssues.length}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col items-start m-2 lg:mr-8 pb-24">
      {/* Review Modal */}
      <ValidationReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        validationResults={getAllValidationIssues()}
        formData={formData}
        setFormData={setFormData}
        isPublishMode={!!formData?.additionalData?.isPublish}
        onGoToStep={goToStep}
        onConfirm={handleConfirmedSubmit}
        isSubmitting={isSubmitting}
        isLong={isLong}
        mediaDraft={mediaDraft}
      />

      {!false && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50 py-3 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {hasDraft && lastSaved && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FaSave className="text-blue-500" />
                  <span>
                    Saved{" "}
                    {new Date().getTime() - lastSaved.getTime() < 60000
                      ? "just now"
                      : `${Math.floor((new Date().getTime() - lastSaved.getTime()) / 60000)} min ago`}
                  </span>
                </div>
              )}
              {!hasDraft && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <FaSave className="text-gray-400" />
                  <span>Auto-save enabled</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={clearDraft}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
            >
              <FaTrash />
              Clear Draft
            </button>
          </div>
        </div>
      )}

      {showDraftRestored && (
        <div className="w-full mb-4 bg-green-50 border border-green-300 rounded-lg p-4 flex items-start gap-3">
          <FaCheckCircle className="text-green-600 text-xl flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-green-800">Draft Restored!</p>
            <p className="text-sm text-green-700">
              Your previously saved work has been restored. Continue where you left off.
            </p>
          </div>
          <button
            onClick={() => setShowDraftRestored(false)}
            className="text-green-600 hover:text-green-800 font-bold"
          >
            ×
          </button>
        </div>
      )}

      <div className="flex items-center justify-between w-full mb-8">
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 transition flex items-center justify-center"
          >
            <FaAngleLeft size={18} />
          </button>
          <button
            onClick={() => navigate("/events")}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium transition"
          >
            Events
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600 font-medium">Create Event</span>
          {formData?.eventName?.trim() && (
            <>
              <span className="text-gray-400">/</span>
              <span className="text-gray-800 font-semibold text-base truncate max-w-[300px]" title={formData.eventName}>
                {formData.eventName}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="w-full mb-6">
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => {
            const idx = i + 1;
            return <StepPill key={idx} index={idx} label={labelAt(idx)} />;
          })}
        </div>
        <div className="w-full h-1 bg-gray-200 rounded mt-3">
          <div
            className="h-1 bg-blue-600 rounded transition-all"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      <form className="w-full bg-white p-4 lg:p-6 rounded-lg shadow-md">
        {renderCurrentStep()}
      </form>

      <div className="w-full flex items-center gap-3 justify-between mt-8 mb-20">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Previous
        </button>
        <div className="flex items-center gap-3">
          {currentStep < totalSteps && (
            <>
              {getStepValidationIssues(currentStep).length > 0 && (
                <button
                  type="button"
                  onClick={() => nextStep(true)}
                  className="px-4 py-2 border border-yellow-400 text-yellow-700 rounded-lg hover:bg-yellow-50"
                >
                  Skip for now
                </button>
              )}
              <button
                type="button"
                onClick={() => nextStep()}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Next
              </button>
            </>
          )}
          {currentStep === totalSteps && (
            <>
              {/* Show Review button when there are issues or when in publish mode */}
              {(allIssues.length > 0 || formData?.additionalData?.isPublish) && (
                <button
                  type="button"
                  onClick={handleSubmitClick}
                  className="px-6 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
                >
                  Review Details
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  const isPublishMode = !!formData?.additionalData?.isPublish;
                  const issues = getAllValidationIssues();
                  if (isPublishMode && issues.length > 0) {
                    setShowReviewModal(true);
                  } else {
                    handleCreateSubmit();
                  }
                }}
                disabled={isSubmitting || (!!formData?.additionalData?.isPublish && hasBlockingIssues)}
                className={`px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2 ${formData?.additionalData?.isPublish
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
              >
                {isSubmitting && <FaSpinner className="animate-spin" />}
                {isSubmitting
                  ? "Creating..."
                  : formData?.additionalData?.isPublish
                    ? "Publish Event"
                    : "Save as Draft"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEventForm;
